import React from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Link, router, useForm } from '@inertiajs/react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type Document = {
  id?: number;
  key: string;
  title: string;
  content: string;
  document_type: string;
  published: boolean;
  community_section_key?: string | null;
  section_display_name?: string;
};

type Props = {
  document: Document;
  document_types: { value: string; label: string }[];
  section_options?: { value: string; label: string }[];
  errors?: string[];
};

export default function DocumentForm({ document, document_types, section_options, errors }: Props) {
  const { data, setData, processing, submit } = useForm({
    key: document.key || '',
    title: document.title || '',
    content: document.content || '',
    document_type: document.document_type || 'terms_of_agreement',
    published: document.published ?? true,
    community_section_key: document.community_section_key || 'none',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Convert 'none' back to null before sending to server
    const formData = {
      ...data,
      community_section_key: data.community_section_key === 'none' ? null : data.community_section_key
    };
    
    if (document.id) {
      router.patch(`/admin/documents/${document.id}`, formData);
    } else {
      router.post('/admin/documents', formData);
    }
  };

  // Generate key from title if not set
  const generateKey = () => {
    if (!data.title) return;
    const key = data.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
    setData('key', key);
  };

  return (
    <AdminLayout title={document.id ? 'Edit Document' : 'New Document'}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            href="/admin/documents"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Documents
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-gray-900">
            {document.id ? 'Edit Document' : 'New Document'}
          </h1>
        </div>

        {/* Error messages */}
        {errors && errors.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm font-medium text-red-800">Please fix the following errors:</p>
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white shadow rounded-lg p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                type="text"
                value={data.title}
                onChange={(e) => setData('title', e.target.value)}
                onBlur={generateKey}
                required
                className="mt-1"
                placeholder="e.g., Pre-Grad Mentor Terms of Agreement"
              />
            </div>

            <div>
              <Label htmlFor="key">Key *</Label>
              <Input
                id="key"
                type="text"
                value={data.key}
                onChange={(e) => setData('key', e.target.value)}
                required
                className="mt-1"
                placeholder="e.g., pregrad_mentor_terms"
                pattern="[a-z0-9_]+"
              />
              <p className="mt-1 text-xs text-gray-500">
                Unique identifier using only lowercase letters, numbers, and underscores
              </p>
            </div>

            <div>
              <Label htmlFor="document_type">Document Type *</Label>
              <Select
                value={data.document_type}
                onValueChange={(value) => setData('document_type', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {document_types.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="community_section">Display in Community Section</Label>
              <Select
                value={data.community_section_key || 'none'}
                onValueChange={(value) => setData('community_section_key', value === 'none' ? null : value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select community section" />
                </SelectTrigger>
                <SelectContent>
                  {section_options?.map((section) => (
                    <SelectItem key={section.value} value={section.value}>
                      {section.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-gray-500">
                Choose which section this document appears in on the Community page
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Label htmlFor="published" className="text-base font-medium">
                  Published
                </Label>
                <Switch
                  id="published"
                  checked={data.published}
                  onCheckedChange={(checked) => setData('published', checked)}
                />
              </div>
              <p className="text-sm text-gray-500">
                {data.published ? 'Document is visible' : 'Document is hidden'}
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="content">Content *</Label>
            <Textarea
              id="content"
              value={data.content}
              onChange={(e) => setData('content', e.target.value)}
              required
              rows={20}
              className="mt-1 font-mono text-sm"
              placeholder="Enter document content in Markdown format..."
            />
            <p className="mt-1 text-xs text-gray-500">
              Supports Markdown formatting. Use # for headings, **bold**, *italic*, - for lists, etc.
            </p>
          </div>

          <div className="flex justify-between items-center pt-6 border-t">
            <div className="text-sm text-gray-500">
              <p>Tips:</p>
              <ul className="list-disc list-inside mt-1">
                <li>Use ## for main sections, ### for subsections</li>
                <li>Use - for bullet points</li>
                <li>Use **text** for bold emphasis</li>
              </ul>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" asChild>
                <Link href="/admin/documents">Cancel</Link>
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Saving...' : 'Save Document'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}